import React from 'react';
import SupportIcon from '../icons/SupportIcon';
import type { SupportAttachmentDTO, SupportLocalMessage } from '../../types/support';
import SupportAttachmentView from './SupportAttachmentView';

interface Props {
  message: SupportLocalMessage;
  showTimestamp: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  onRetry?: (localId: string) => void;
  onDelete?: (localId: string) => void;
  onOpenLightbox?: (url: string) => void;
}

const formatTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const pendingToDto = (
  p: NonNullable<SupportLocalMessage['pendingAttachments']>[number],
): SupportAttachmentDTO => ({
  id: p.localId,
  url: '',
  kind: p.kind,
  mimeType: p.mimeType,
  size: p.size,
  originalName: p.name,
  width: null,
  height: null,
  durationSec: p.durationSec ?? null,
});

const SupportMessageBubble: React.FC<Props> = ({
  message,
  showTimestamp,
  isFirstInGroup,
  isLastInGroup,
  onRetry,
  onDelete,
  onOpenLightbox,
}) => {
  const isUser = message.direction === 'user_to_admin';
  const isFailed = message.status === 'failed';
  const isSending = message.status === 'sending';

  const hasAttachments =
    (message.attachments && message.attachments.length > 0) ||
    (message.pendingAttachments && message.pendingAttachments.length > 0);

  const onlyAttachments = hasAttachments && !message.text;

  const bubbleClass = [
    'sc-bubble',
    isUser ? 'sc-bubble--user' : 'sc-bubble--admin',
    isFirstInGroup ? 'sc-bubble--first' : '',
    isLastInGroup ? 'sc-bubble--last' : '',
    onlyAttachments ? 'sc-bubble--only-attachments' : '',
    isFailed ? 'sc-bubble--failed' : '',
    isSending ? 'sc-bubble--sending' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`sc-row ${isUser ? 'sc-row--user' : 'sc-row--admin'}`}>
      {!isUser && isFirstInGroup && (
        <div className="sc-avatar" aria-hidden="true">
          <SupportIcon size={18} strokeWidth={2} />
        </div>
      )}
      {!isUser && !isFirstInGroup && <div className="sc-avatar sc-avatar--ghost" aria-hidden="true" />}

      <div className="sc-bubble-wrap">
        {/* {!isUser && isFirstInGroup && (
          <div className="sc-admin-name">
            {message.telegramAdminUsername || 'Поддержка'}
          </div>
        )} */}

        <div className={bubbleClass}>
          {hasAttachments && (
            <div className={`sc-attachments ${onlyAttachments ? 'sc-attachments--alone' : ''}`}>
              {message.attachments.map((att) => (
                <div key={att.id} className="sc-attachment-item">
                  <SupportAttachmentView
                    attachment={att}
                    variant={isUser ? 'user' : 'admin'}
                    onOpenLightbox={onOpenLightbox}
                  />
                </div>
              ))}
              {message.pendingAttachments?.map((p) => (
                <div key={p.localId} className="sc-attachment-item sc-attachment-item--pending">
                  <SupportAttachmentView
                    attachment={pendingToDto(p)}
                    directUrl={p.previewUrl}
                    variant={isUser ? 'user' : 'admin'}
                    onOpenLightbox={onOpenLightbox}
                  />
                </div>
              ))}
            </div>
          )}

          {message.text && <div className="sc-text">{message.text}</div>}

          {isLastInGroup && (
            <div className="sc-meta">
              {isSending && (
                <span className="sc-meta-status" title="Отправка...">
                  <span className="sc-meta-spinner" aria-hidden="true" />
                </span>
              )}
              {!isSending && !isFailed && (
                <span className="sc-meta-time">{formatTime(message.createdAt)}</span>
              )}
              {!isSending && !isFailed && isUser && (
                <svg className="sc-meta-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {isFailed && (
                <span className="sc-meta-error" title={message.errorMessage}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Не отправлено
                </span>
              )}
            </div>
          )}
        </div>

        {showTimestamp && !isLastInGroup && (
          <div className="sc-time-outside">{formatTime(message.createdAt)}</div>
        )}

        {isFailed && (
          <div className="sc-failed-actions">
            {onRetry && (
              <button
                type="button"
                className="sc-failed-btn"
                onClick={() => onRetry(message.localId)}
              >
                Повторить
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="sc-failed-btn sc-failed-btn--ghost"
                onClick={() => onDelete(message.localId)}
              >
                Удалить
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportMessageBubble;
